import { createApp } from 'vue';
import { createWebHashHistory, createRouter } from 'vue-router';

import App from './App.vue';

import NotFound from './routes/NotFound.vue';
import Question from './routes/Question.vue';
import EmailOptIn from './routes/EmailOptIn.vue';
import Results from './routes/Results.vue';

export default class Quiz {
    constructor (el) {
        if (!el) return;

        this.config = Object.assign(JSON.parse(document.getElementById('quizConfig').text), { navigation: { secondary: false } });
        const globalEls = document.querySelectorAll('.js-quiz-global-els');
        const templateEls = document.querySelector('.js-quiz-template-els-container');

        /**
         * Build routes from ONLY top-level questions.
         * `meta.back` points to the previous question's id.
         */
        function buildRoutes(cmsData, opts = {}) {
            const {
                stepComponent = Question,
                resultsPathSuffix = ':emailOptIn/results',
                notFoundTitle = 'Not Found',
            } = opts
            
            const linear = (cmsData && cmsData.questions ? cmsData.questions : []).filter(Boolean)
            const order = linear.map(q => q.id) // question ids (route names)
            
            // Turn an id like 'how-would-you-rate-your-stress' into a valid param key
            const toParamKey = (id) => id.replace(/[^\w]/g, '_') // letters/numbers/underscore only
            
            // Use ID-derived keys instead of q1/q2...
            const paramKeys = order.map(id => toParamKey(id))
            
            // Helper to build '/:<key1>/:<key2>/...'
            const buildParamChain = (count) => {
                if (count <= 0) return ''
                const segs = paramKeys.slice(0, count).map(k => `:${k}`)
                return '/' + segs.join('/')
            }
            
            // Step routes with cumulative params
            const stepRoutes = linear.map((question, idx) => {
                const isFirst = idx === 0
                const path = isFirst ? '/' : buildParamChain(idx) // step N expects previous params set
            
                return {
                    path,
                    name: question.id, // route name stays as original id (hyphenated)
                    component: stepComponent,
                    props: () => ({ question, index: idx }),
                    meta: {
                        title: question.title,
                        back: idx > 0 ? linear[idx - 1].id : null,
                        order,
                        firstId: order[0],
                        lastId: order[order.length - 1],
                        // write into the ID-derived param key (e.g., 'how_would_you_rate_your_stress')
                        writeKey: paramKeys[idx],
                        klaviyoKey: question.klaviyoKey
                    },
                }
            })
            
            // Email & Results: keep existing behavior
            const optionalParamChain = buildParamChain(paramKeys.length) // keeping your current helper signature
            
            const emailRoute = {
                path: `${optionalParamChain}`,
                name: 'EmailOptIn',
                component: EmailOptIn,
                meta: {
                    back: linear.length ? linear[linear.length - 1].id : null,
                    order,
                    firstId: order[0],
                    lastId: order[order.length - 1],
                },
            }
            
            const resultsRoute = {
                path: `${optionalParamChain}/${resultsPathSuffix}`,
                name: 'Results',
                component: Results,
                meta: {
                    back: 'EmailOptIn',
                    order,
                    firstId: order[0],
                    lastId: order[order.length - 1],
                },
            }
            
            const notFoundRoute = {
                path: '/:pathMatch(.*)*',
                name: 'NotFound',
                component: NotFound,
                meta: { title: notFoundTitle },
            }
            
            return [...stepRoutes, emailRoute, resultsRoute, notFoundRoute]
        }

        const routes = buildRoutes(this.config.quizData);

        this.router = createRouter ({
            history: createWebHashHistory(),
            routes: routes,
            scrollBehavior(to, from, savedPosition) {
                if (savedPosition) {
                    return savedPosition;
                } else {
                    return { top: 0 }
                }
            }
        });

        this.router.afterEach((to) => {
            if (to.name !== 'Results' && window.innerWidth >= 1024) {
                document.querySelector('.js-route-content-container')?.scrollTo(0, 0);
            }

            if (to.name === 'Results') {
                globalEls.forEach(element => {
                    element.classList.remove('!hidden');
                });
                templateEls.classList.remove('hide-quiz-template-els');
            } else {
                globalEls.forEach(element => {
                    element.classList.add('!hidden');
                });
                templateEls.classList.add('hide-quiz-template-els');
            }
        });

        // Updates page `<title>`
        const rootTitle = document.title;

        if (rootTitle) {
            this.router.afterEach(to => {
                document.title = to.meta?.title ? `${to.meta.title} - ${rootTitle}` : rootTitle;
            });
        }

        this.init();
    }

    init() {
        createApp(App)
            .use(this.router)
            .mount('#app-quiz');
    }
}
